import {
  CheckCircle2,
  XCircle,
  Loader2,
  UserCheck,
  Square,
  ArrowRight,
  Package,
  Hand,
} from 'lucide-react';
import { useAgentStudioNavigate } from '../../lib/routes';
import { Drawer } from '../../components/ui/Drawer';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { OVERSIGHT_BY_MODE, NODE_DEF_BY_KIND } from '../../lib/catalog';
import { useFlowStore } from '../../store/flowStore';
import type { HumanOversightMode, RunEvent } from '../../types';
import type { useFlowSimulation } from './useFlowSimulation';

type Sim = ReturnType<typeof useFlowSimulation>;

export function RunPanel({
  open,
  onClose,
  sim,
  oversightMode,
  flowName,
}: {
  open: boolean;
  onClose: () => void;
  sim: Sim;
  oversightMode: HumanOversightMode;
  flowName: string;
}) {
  const navigate = useAgentStudioNavigate();
  const nodes = useFlowStore((s) => s.nodes);
  const { state, abort } = sim;
  const oDef = OVERSIGHT_BY_MODE[oversightMode];

  function nodeLabel(nodeId: string) {
    const n = nodes.find((x) => x.id === nodeId);
    if (!n) return 'Fluxo';
    return (n.data.config.label as string) || NODE_DEF_BY_KIND[n.data.kind].label;
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      height="h-[58vh]"
      side="bottom"
      title={
        <div className="flex w-full items-center justify-between pr-2">
          <div className="flex items-center gap-3">
            <span>Execução · {flowName}</span>
            {state.running ? (
              <Badge tone="info" dot>Em andamento</Badge>
            ) : state.pausedForHuman ? (
              <Badge tone="orange" dot>Pausado p/ revisão</Badge>
            ) : state.finishedRun ? (
              <Badge tone={state.finishedRun.status === 'success' ? 'success' : 'danger'} dot>
                {state.finishedRun.status === 'success' ? 'Concluído' : 'Erro'}
              </Badge>
            ) : null}
            <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: oDef.color }}>
              <oDef.icon className="h-3.5 w-3.5" /> {oDef.label}
            </span>
          </div>

          {state.running && (
            <Button size="sm" variant="ghost" icon={Square} onClick={abort}>Parar de exibir</Button>
          )}
        </div>
      }
    >
      <div className="grid h-full grid-cols-1 gap-0 lg:grid-cols-[1fr_340px]">
        {/* Log streaming (passo a passo) */}
        <div className="overflow-y-auto scrollbar-thin border-r border-line p-4">
          <div className="space-y-1.5 font-mono text-xs">
            {state.events.length === 0 && <p className="text-slate">Aguardando eventos…</p>}
            {state.events.map((e, i) => (
              <LogLine key={i} event={e} label={nodeLabel(e.nodeId)} />
            ))}
          </div>
        </div>

        {/* Painel lateral: pausa / resultado / saída final */}
        <div className="overflow-y-auto scrollbar-thin p-4">
          {state.pausedForHuman ? (
            <PausedCard onOpen={() => navigate('/activities')} />
          ) : state.finishedRun ? (
            <FinishedSummary run={state.finishedRun} onOpen={() => navigate(`/runs/${state.finishedRun!.id}`)} />
          ) : (
            <div className="rounded-xl border border-line bg-cloud p-4 text-center text-xs text-slate">
              {state.running ? 'Executando os nós do fluxo…' : 'Clique em Executar e informe a entrada.'}
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}

function LogLine({ event, label }: { event: RunEvent; label: string }) {
  const Icon =
    event.status === 'done'
      ? CheckCircle2
      : event.status === 'error'
        ? XCircle
        : event.status === 'awaiting_human'
          ? UserCheck
          : Loader2;
  const color =
    event.status === 'done'
      ? 'text-success'
      : event.status === 'error'
        ? 'text-danger'
        : event.status === 'awaiting_human'
          ? 'text-amber'
          : event.status === 'running'
            ? 'text-brand-orange'
            : 'text-slate';
  const time = new Date(event.timestamp).toLocaleTimeString('pt-BR');
  return (
    <div className="animate-fade-in">
      <div className="flex items-start gap-2">
        <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${color} ${event.status === 'running' ? 'animate-spin' : ''}`} />
        <span className="text-slate">{time}</span>
        <span className="font-semibold text-ink">{label}{event.iteration ? ` #${event.iteration}` : ''}</span>
        <span className="text-slate">— {event.message}</span>
      </div>
      {event.output && event.output.trim() && (
        <details className="ml-[22px] mt-0.5">
          <summary className="cursor-pointer select-none text-[10px] font-medium text-brand-orange hover:underline">
            saída do nó
          </summary>
          <pre className="mt-1 max-h-40 overflow-auto scrollbar-thin whitespace-pre-wrap rounded-lg bg-cloud p-2 text-[10px] text-ink">
            {event.output}
          </pre>
        </details>
      )}
    </div>
  );
}

function PausedCard({ onOpen }: { onOpen: () => void }) {
  return (
    <div className="animate-fade-in rounded-xl border-2 border-amber bg-amber/5 p-4">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
        <Hand className="h-4 w-4 text-amber" /> Pausado para revisão humana
      </div>
      <p className="text-xs text-slate">
        O fluxo atingiu um nó de revisão e está aguardando uma decisão. A aprovação acontece em
        <strong className="text-ink"> Minhas Atividades</strong>; ao decidir, o fluxo retoma de onde parou.
      </p>
      <Button variant="primary" size="sm" iconRight={ArrowRight} className="mt-3" onClick={onOpen}>
        Ir para Minhas Atividades
      </Button>
    </div>
  );
}

function FinishedSummary({ run, onOpen }: { run: NonNullable<ReturnType<typeof useFlowSimulation>['state']['finishedRun']>; onOpen: () => void }) {
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-line bg-white p-4">
        <p className="text-sm font-semibold text-ink">Resumo da execução</p>
        <p className="mt-1 text-xs text-slate">{run.events.length} eventos · {run.artifacts.length} artefato(s)</p>
        {run.costBRL != null && (
          <p className="mt-1 text-xs text-slate">
            Custo: <strong className="text-ink">{run.costBRL.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 4 })}</strong>
          </p>
        )}
        <Button variant="secondary" size="sm" iconRight={ArrowRight} className="mt-3" onClick={onOpen}>Ver detalhe da execução</Button>
      </div>

      {run.finalOutput && (
        <div className="rounded-xl border border-line bg-white p-4">
          <p className="mb-2 text-sm font-semibold text-ink">Saída final</p>
          <pre className="max-h-40 overflow-auto scrollbar-thin whitespace-pre-wrap rounded-lg bg-cloud p-2.5 text-[11px] text-slate">{run.finalOutput}</pre>
        </div>
      )}

      {run.artifacts.length > 0 && (
        <div className="rounded-xl border border-line bg-white p-4">
          <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-ink">
            <Package className="h-4 w-4 text-navy" /> Artefatos gerados
          </p>
          <div className="space-y-1.5">
            {run.artifacts.map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-lg bg-cloud px-2.5 py-1.5 text-xs">
                <span className="font-medium text-ink">{a.name}</span>
                <Badge tone="neutral">{a.type}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
